import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "@/hooks/useAuth";
import { api, type GettingStartedStateOut } from "@/services/api";
import {
  ALL_TASK_KEYS,
  REFERRALS_TO_COMPLETE,
  GETTING_STARTED_PHASES,
  TOTAL_STEP_COUNT,
  type GettingStartedPhase,
  type GettingStartedTaskKey,
} from "@/constants/gettingStarted";
import { toStarEntries } from "@/utils/starPortfolio";

/**
 * Completion state for the XO Getting Started journey.
 *
 * TWO KINDS OF "DONE" are stitched together here, and the difference is the
 * whole design:
 *
 *  - DERIVED tasks read data the platform already holds. `xo.activate.stars`
 *    is true when the expert has S.T.A.R. stories on their capabilities
 *    record. Nothing is stored, and the tick cannot drift from reality,
 *    because it IS reality.
 *
 *  - ACKNOWLEDGED tasks are a claim the platform cannot verify, so the claim
 *    is the record and is persisted per key by the backend.
 *
 * Callers should not care which is which. `isDone` answers for both.
 *
 * PHASES are not gated: every phase is open, so an expert can move between
 * them and finish steps in whatever order suits them. A phase is complete when
 * every NON-OPTIONAL step in it is done; the "current" phase is simply the
 * first one that is not.
 */

const STATE_KEY = (userId: string) => ["getting-started", userId] as const;

/** Drops anything the client no longer knows about, so a retired key can't
 *  leave a permanently-ticked ghost behind. */
const knownOnly = (keys: string[] | undefined): GettingStartedTaskKey[] => {
  if (!Array.isArray(keys)) return [];
  return keys.filter((k): k is GettingStartedTaskKey =>
    (ALL_TASK_KEYS as string[]).includes(k),
  );
};

/**
 * @param options.enabled — false skips every fetch (the always-mounted sidebar
 *   passes false outside the XO workspace, where none of this applies).
 */
export function useGettingStarted(options: { enabled?: boolean } = {}) {
  const { user } = useAuth();
  // Gating the user id gates every query below, which are all keyed on it.
  const userId = options.enabled === false ? "" : (user?.id ?? "");
  const queryClient = useQueryClient();

  // ── Derived: S.T.A.R. stories, and the review gate on the welcome screen ──
  // 404 is the normal "hasn't started an application yet" response, not an
  // error worth surfacing — it simply means no stories and no review status.
  // Same key as the profile's Expert Contribution tab: a S.T.A.R. saved there
  // invalidates it, so it shows here straight away instead of after this
  // query's own cache happens to expire.
  const { data: capabilities, isLoading: capabilitiesLoading } = useQuery({
    queryKey: ["expert-application-capabilities"],
    queryFn: () => api.capabilities.getMine().catch(() => null),
    staleTime: 60 * 1000,
    enabled: !!userId,
  });

  const starCount = toStarEntries(capabilities?.starPortfolio).length;

  // The named referral lists, which drive the two Phase 3 steps' completion.
  const { data: referrals } = useQuery({
    queryKey: ["getting-started-referrals"],
    queryFn: () => api.gettingStarted.getReferrals(),
    enabled: !!userId,
    staleTime: 60 * 1000,
  });
  // The three onboarding steps the XO Home checklist used to hold are read
  // from the user's own record — the same query key that page used, so it is
  // shared and cached rather than fetched twice.
  const { data: advisor, isLoading: advisorLoading } = useQuery({
    queryKey: ["advisor-me"],
    queryFn: () => api.advisors.getAdvisor(),
    staleTime: 5 * 60 * 1000,
    enabled: !!userId,
  });
  const ftuxBits = advisor?.ftuxStep ?? 0;
  // The three rows of the Code of Conduct step, as the XO Home checklist kept
  // them: sign-off is its own flag; the PDFs are bits 1 and 2 of ftux_step.
  const onboarding = {
    conductSigned: !!advisor?.codeOfConductAccepted,
    explainerRead: (ftuxBits & 2) !== 0,
    archetypesRead: (ftuxBits & 4) !== 0,
  };

  const achieverReferrals = referrals?.achievers.length ?? 0;
  const xoReferrals = referrals?.xos.length ?? 0;
  const reviewStatus = capabilities?.reviewStatus;

  // ── Acknowledged: what the server has recorded ───────────────────────────
  const { data: state, isLoading: stateLoading } = useQuery({
    queryKey: STATE_KEY(userId),
    queryFn: () => api.gettingStarted.getMine(),
    enabled: !!userId,
    staleTime: 60 * 1000,
  });

  const acknowledged = knownOnly(state?.completed);
  const welcomed = state?.welcomed ?? false;

  /** Every mutation returns the whole state, so the cache can be replaced
   *  outright rather than invalidated and refetched. */
  const writeState = (next: GettingStartedStateOut) => {
    queryClient.setQueryData(STATE_KEY(userId), next);
  };

  const completeMutation = useMutation({
    mutationFn: (taskKey: GettingStartedTaskKey) => api.gettingStarted.complete(taskKey),
    onSuccess: writeState,
  });

  const toggleMutation = useMutation({
    mutationFn: (taskKey: GettingStartedTaskKey) =>
      acknowledged.includes(taskKey)
        ? api.gettingStarted.uncomplete(taskKey)
        : api.gettingStarted.complete(taskKey),
    onSuccess: writeState,
  });

  const uncompleteMutation = useMutation({
    mutationFn: (taskKey: GettingStartedTaskKey) => api.gettingStarted.uncomplete(taskKey),
    onSuccess: writeState,
  });

  /**
   * Records a mandatory onboarding step on the user's FTUX record: 1 = Code of
   * Conduct signed (also sets code_of_conduct_accepted), 2 = explainer read,
   * 3 = archetypes read. Refreshes the record so the tick follows.
   */
  const onboardingMutation = useMutation({
    mutationFn: (ftuxStep: 1 | 2 | 3) => api.advisors.completeFtuxStep(ftuxStep),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["advisor-me"] }),
  });

  const welcomedMutation = useMutation({
    mutationFn: () => api.gettingStarted.markWelcomed(),
    onSuccess: writeState,
  });

  /**
   * "First-time view" — clears every tick AND re-arms the welcome screen.
   * This is what the server's DELETE does on its own.
   */
  const replayWelcomeMutation = useMutation({
    mutationFn: () => api.gettingStarted.reset(),
    onSuccess: writeState,
  });

  /**
   * "Reset" — clears the ticks but keeps the welcome screen dismissed.
   *
   * Two calls, awaited in order rather than fired side by side: the reset
   * clears `welcomed` as part of its job, so a parallel markWelcomed could
   * land first and be undone by it.
   */
  const resetMutation = useMutation({
    mutationFn: async () => {
      await api.gettingStarted.reset();
      return api.gettingStarted.markWelcomed();
    },
    onSuccess: writeState,
  });

  /**
   * Done because the platform can SEE it is done, not because anyone claimed
   * it. Only `xo.activate.stars` qualifies: the S.T.A.R. portfolio either has
   * entries or it does not.
   *
   * Callers use this to suppress the manual controls — un-ticking something
   * the data says is true would not survive the next render, so offering the
   * control would be a lie.
   */
  const isAutoDone = (taskKey: GettingStartedTaskKey): boolean => {
    // Mandatory onboarding: done once all three checklist rows are, read
    // straight from the user record.
    if (taskKey === "xo.activate.conduct") {
      return onboarding.conductSigned && onboarding.explainerRead && onboarding.archetypesRead;
    }
    if (taskKey === "xo.activate.stars") return starCount > 0;
    // ONE is enough, though the step asks for three — referring is a favour,
    // not a gate. Dropping back to zero un-ticks it, unless it was also
    // ticked by hand; isDone ORs the two.
    if (taskKey === "xo.connect.refer-achievers") return achieverReferrals >= REFERRALS_TO_COMPLETE;
    if (taskKey === "xo.connect.refer-xos") return xoReferrals >= REFERRALS_TO_COMPLETE;
    return false;
  };

  /**
   * Derivation ADDS a way to be done; it never removes the manual one, so a
   * derived step whose data has not caught up can still be ticked by hand.
   */
  const isDone = (taskKey: GettingStartedTaskKey): boolean =>
    isAutoDone(taskKey) || acknowledged.includes(taskKey);

  const phases = GETTING_STARTED_PHASES;

  const phaseDoneCount = (phase: GettingStartedPhase) =>
    phase.tasks.filter((t) => isDone(t.key)).length;

  /**
   * Optional steps do NOT count against a phase. `xo.connect.peer` is the only
   * one today; skipping it still completes Phase 3.
   */
  const phaseComplete = (phase: GettingStartedPhase) => {
    const required = phase.tasks.filter((t) => !t.optional);
    return required.length > 0 && required.every((t) => isDone(t.key));
  };

  /** Index of the first unfinished phase — the one the expert is up to. */
  const openPhaseIndex = (() => {
    let i = 0;
    while (i < phases.length - 1 && phaseComplete(phases[i])) i += 1;
    return i;
  })();

  /** The first step in a phase that is not yet done — where the view should open. */
  const firstUndoneKey = (phase: GettingStartedPhase): GettingStartedTaskKey =>
    (phase.tasks.find((t) => !isDone(t.key)) ?? phase.tasks[0]).key;

  // Progress is measured against the WHOLE journey, as the design reports it
  // ("2 of 15 steps done") — not against the phase you happen to be looking at.
  const completedCount = phases.reduce((n, p) => n + phaseDoneCount(p), 0);
  const overallPct =
    TOTAL_STEP_COUNT === 0 ? 0 : Math.round((completedCount / TOTAL_STEP_COUNT) * 100);

  const currentPhase = phases[openPhaseIndex];
  const allComplete = phases.every((p) => phaseComplete(p));

  return {
    phases,
    currentPhase,
    openPhaseIndex,
    firstUndoneKey,
    isDone,
    isAutoDone,
    complete: completeMutation.mutate,
    /** The Code of Conduct step's three rows — see `onboarding` above. */
    onboarding,
    /** Records one of those rows — see onboardingMutation. */
    recordOnboarding: onboardingMutation.mutateAsync,
    isRecordingOnboarding: onboardingMutation.isPending,
    /**
     * Tick or untick by hand, as the design's checkbox does. Only meaningful
     * for `acknowledged` tasks: a derived tick reflects real data, so there is
     * nothing to toggle and the caller must not offer it.
     */
    toggle: toggleMutation.mutate,
    uncomplete: uncompleteMutation.mutate,
    /** Optional steps only. Skipping is recorded as done — the phase moves on. */
    skip: completeMutation.mutate,
    /** Clears the ticks, keeps the welcome screen dismissed. */
    reset: resetMutation.mutate,
    /** Clears the ticks and shows the welcome screen again. */
    replayWelcome: replayWelcomeMutation.mutate,
    welcomed,
    markWelcomed: welcomedMutation.mutate,
    phaseDoneCount,
    phaseComplete,
    completedCount,
    totalCount: TOTAL_STEP_COUNT,
    overallPct,
    allComplete,
    starCount,
    achieverReferrals,
    xoReferrals,
    /**
     * The capability review gate on the welcome screen's "Get Started".
     * A missing status (no application yet, or a 404) counts as NOT approved —
     * the flow should not be startable before the application is accepted.
     */
    reviewStatus,
    isApproved: reviewStatus === "APPROVED",
    // The onboarding record too: without it the three mandatory steps would
    // read as undone for a moment and the view would open on the wrong step.
    isLoading: capabilitiesLoading || stateLoading || advisorLoading,
  };
}
